import { planFilterForCase } from '~/utils/planDays'

/**
 * COMPANY CASES (/company-cases) — the pure decisions inside "which case is
 * selected" (`composables/useCaseSelection.js` runs the rules 1–4 in order and
 * owns the side effects). Extracted in the /company-cases refactor
 * (2026-10-05), behaviour unchanged.
 */

/** The case with this id in a list, or null. */
export const caseById = (list, id) =>
  id != null ? list.find(c => String(c.id) === String(id)) ?? null : null

/**
 * Where to go when the selected case is no longer visible:
 *   - nothing visible → null;
 *   - nothing was selected, or it wasn't in the old list → the first visible;
 *   - the old and new lists share NO case (a different filter / day) → the
 *     first visible (a position means nothing across unrelated lists);
 *   - otherwise (one case left the same list) → the case now at its old index,
 *     or the one before when it was last ("plan / remove the selected one,
 *     advance to the next").
 */
export function pickNextVisible(newVisible, removedId, oldDisplayedIds) {
  if (!newVisible.length) return null
  if (removedId == null) return newVisible[0]

  const oldIndex = oldDisplayedIds.findIndex(id => String(id) === String(removedId))
  if (oldIndex < 0) return newVisible[0]

  const newIds = new Set(newVisible.map(c => String(c.id)))
  const hasOverlap = oldDisplayedIds.some(id => newIds.has(String(id)))
  if (!hasOverlap) return newVisible[0]

  return newVisible[oldIndex] ?? newVisible[oldIndex - 1] ?? newVisible[0]
}

/**
 * A deep-linked case's planning day: move the view to the day it lives on
 * (its planned day, Future, else All) — but NOT from "All", where it is already
 * visible (Sandy 2026-09-01). Returns the plan filter to set, or null to leave
 * it as is.
 */
export function planFilterSwitchFor(currentPlanFilter, target) {
  if ((currentPlanFilter?.kind ?? 'all') === 'all' || !target) return null

  const desired = planFilterForCase(target)

  const same = (currentPlanFilter?.kind ?? 'all') === desired.kind
    && (currentPlanFilter?.iso ?? null) === (desired.iso ?? null)

  return same ? null : desired
}

/**
 * Is a deep-link that keeps missing SPENT? Without a server to ask why, at
 * once; otherwise after `max` misses (a moving scope takes a beat, so the
 * first misses can run against the old list).
 */
export const deepLinkMissSpent = ({ canExplain, attempts, max }) => !canExplain || attempts >= max

/**
 * The page after a case was ERASED from the database (Sandy 2026-11-02): the
 * case leaves the list, nothing is selected, and the pane stays EMPTY — the page
 * does not advance to the neighbour (`pickNextVisible`'s rule 4).
 *
 * Advancing put a legitimate case in front of the user a heartbeat after a
 * destructive action, one click from the same button. The hold ends when the
 * user picks a case themselves, arrives somewhere, or switches company / agency.
 */
export function afterCaseErased(activeCases, selectedCase, caseId) {
  const id = String(caseId)

  return {
    activeCases: (activeCases ?? []).filter(c => String(c.id) !== id),
    selectedCase: String(selectedCase?.id) === id ? null : (selectedCase ?? null),
    holdSelectionEmpty: true,
  }
}
